/**
 * Connectivity heartbeat — resident-app wiring of the shared ConnectivityMonitor.
 *
 * Every 60s it probes the five connectivity layers (network / internet / api /
 * sip / mqtt), buffers the sample locally, and flushes the buffer to the API on
 * a state change or every 15 min (whichever comes first). The buffer is the
 * device's offline memory: if internet is down — exactly when this data matters
 * — samples are retained and delivered once the device recovers.
 */
import { ConnectivityMonitor } from '@caro/shared/telemetry';
import type {
  ConnectivitySample,
  ConnectivityHeartbeat,
  ConnectivityStorage,
} from '@caro/shared/telemetry';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { api } from '../hooks/useAuth';
import { isSipRegistered } from '../hooks/useSip';
import { timeoutSignal } from '../utils/timeoutSignal';
import { syncClockFromServer, clockSkewMs, deviceTimeZone } from '../utils/clock';
import { powerTelemetry } from '../utils/powerStats';

const BUFFER_KEY = 'caro_connectivity_buffer';
const apiBaseUrl = (import.meta as any).env?.VITE_API_URL || 'https://api.caro.care';
const APP_VERSION = (import.meta as any).env?.VITE_APP_VERSION || 'dev';

/**
 * Device state that only React hooks know (battery, permissions, SIP extension,
 * connection type). `probe()` runs at module level on a timer, so the hook layer
 * pushes a snapshot here instead — see useMqttTelemetry's status collector,
 * which already assembles exactly this data.
 *
 * Stale-by-design: whatever the last render observed is good enough for a 60s
 * telemetry sample, and a missing snapshot simply omits the fields.
 */
export interface DeviceStateSnapshot {
  batteryLevel?: number;
  batteryCharging?: boolean;
  sipExtension?: string;
  connectionType?: string;
  permissionsOk?: boolean;
  permissionsMissing?: string[];
}

let deviceState: DeviceStateSnapshot = {};

/** Publish the latest device state for the next connectivity sample. */
export function setDeviceStateSnapshot(snapshot: DeviceStateSnapshot): void {
  deviceState = snapshot;
}

/**
 * The "temp file": a localStorage-backed buffer. It persists across WebView
 * restarts and is more than enough for ~240 small samples. Swap for
 * @capacitor/filesystem if a true on-disk file is ever required — the
 * ConnectivityStorage interface is the only thing that has to change.
 */
const storage: ConnectivityStorage = {
  read: async () => {
    try {
      return localStorage.getItem(BUFFER_KEY);
    } catch {
      return null;
    }
  },
  write: async (data: string) => {
    try {
      localStorage.setItem(BUFFER_KEY, data);
    } catch {
      // quota exceeded / storage unavailable — drop silently, next flush retries
    }
  },
};

/** One probe of all five layers. internet+api use lightweight external fetches. */
async function probe(): Promise<Omit<ConnectivitySample, 'at'>> {
  const network = typeof navigator !== 'undefined' ? navigator.onLine : true;

  // Internet reachability — a public endpoint independent of our API.
  let internet = false;
  try {
    const res = await fetch('https://dns.google/resolve?name=api.caro.care&type=A', {
      signal: timeoutSignal(5000),
    });
    internet = res.ok;
  } catch {
    internet = false;
  }

  // CARO API reachability + round-trip latency.
  let apiOk = false;
  let apiLatencyMs: number | undefined;
  try {
    const t0 = Date.now();
    const res = await fetch(`${apiBaseUrl}${API_ENDPOINTS.HEALTH}`, {
      signal: timeoutSignal(5000),
    });
    const t1 = Date.now();
    apiOk = res.ok;
    apiLatencyMs = t1 - t0;
    if (apiOk) {
      internet = true; // reaching our API proves internet works
      // The health body carries the server's timestamp. Folding it into the app
      // clock is what keeps a tablet with a drifting or mis-zoned clock on
      // Belgian time — see utils/clock.ts. A malformed body is not an outage.
      try {
        const body = (await res.json()) as { timestamp?: unknown } | null;
        syncClockFromServer(body?.timestamp, t0, t1);
      } catch {
        // ignore
      }
    }
  } catch {
    apiOk = false;
  }

  // `mqtt` is deliberately not reported — production never connects the broker,
  // so it was a hard-coded false that showed up as a permanent "MQTT down" on
  // every tablet in the portal. See ConnectivitySample's deprecation note.
  return {
    network,
    internet,
    api: apiOk,
    sip: isSipRegistered(),
    ...(apiLatencyMs !== undefined ? { apiLatencyMs } : {}),
    // Diagnostics for "the clock is wrong on tablet X": how far the device clock
    // is from the server's, and which zone the device thinks it is in.
    clockSkewMs: clockSkewMs(),
    ...(deviceTimeZone() ? { timeZone: deviceTimeZone() } : {}),
    appVersion: APP_VERSION,
    ...deviceState,
    // Na deviceState: de native lezing (APK ≥ 1.0.4) wint van de Battery API.
    ...(await powerTelemetry().catch(() => ({}))),
  };
}

async function flush(heartbeat: ConnectivityHeartbeat): Promise<void> {
  // Must reject on failure so the monitor retains the buffer and retries.
  await api.post(API_ENDPOINTS.DEVICE_CONNECTIVITY, heartbeat);
}

let monitor: ConnectivityMonitor | null = null;

/** Start the heartbeat (idempotent). Call when the device is authenticated. */
export function startConnectivityHeartbeat(): void {
  if (monitor) return;
  monitor = new ConnectivityMonitor(
    { probe, storage, flush },
    { checkIntervalMs: 60_000, keepaliveMs: 900_000 },
  );
  void monitor.start();
}

/** Stop the heartbeat (e.g. on logout). */
export function stopConnectivityHeartbeat(): void {
  monitor?.stop();
  monitor = null;
}
